import React from "react";

/**
 * RoutingStrip — one part's stations as a row of chips: the "shop view" of the
 * SO page's Production Detail (2026-09-08). State is colour, sets ride on the
 * chip, defects flag it. Every chip, the shared laser sheet / saw list included,
 * uses the same flow: green done, blue active, amber today, red late, dashed
 * pending (a reserved yellow for "shared" read like "due today" — 2026-09-09).
 * Pure presentation: the caller derives each step's `state` and handles clicks.
 *
 * The SO Line Items table (2026-10-20) uses the count states: green done,
 * yellow partial (some of the order's pieces through), gray zero (none there
 * yet), a bold `qty` on each chip and `compact` chips. Outside steps
 * (2026-10-20): blue "out" with a truck while pieces are at the vendor, red
 * border once overdue ("out_late"), gray dashed "staged" on a draft manifest;
 * `qtyLabel` replaces the bare number ("200 back · 430 out").
 *
 * step = { key, label, sub?, qty?, qtyLabel?, title?, state, sets?: {done, total}, defects?: n }
 */
export const CHIP_STATES = {
  done: { bg: "var(--bs-success-bg-subtle)", border: "var(--bs-success)", fg: "var(--bs-success-text-emphasis)", dashed: false },
  active: { bg: "var(--bs-primary)", border: "var(--bs-primary)", fg: "#fff", dashed: false },
  today: { bg: "var(--bs-warning-bg-subtle)", border: "var(--bs-warning)", fg: "var(--bs-warning-text-emphasis)", dashed: false },
  next: { bg: "var(--bs-warning-bg-subtle)", border: "var(--bs-warning-border-subtle)", fg: "var(--bs-warning-text-emphasis)", dashed: true },
  late: { bg: "var(--bs-danger-bg-subtle)", border: "var(--bs-danger)", fg: "var(--bs-danger-text-emphasis)", dashed: false },
  blocked: { bg: "var(--bs-secondary-bg-subtle)", border: "var(--bs-secondary-border-subtle)", fg: "var(--bs-secondary-color)", dashed: true },
  pending: { bg: "var(--bs-body-bg)", border: "var(--bs-border-color)", fg: "var(--bs-secondary-color)", dashed: true },
  cancelled: { bg: "var(--bs-secondary-bg-subtle)", border: "var(--bs-border-color)", fg: "var(--bs-secondary-color)", dashed: true, strike: true },
  join: { bg: "transparent", border: "var(--bs-border-color)", fg: "var(--bs-secondary-color)", dashed: true },
  partial: { bg: "var(--bs-warning-bg-subtle)", border: "var(--bs-warning)", fg: "var(--bs-warning-text-emphasis)", dashed: false },
  zero: { bg: "var(--bs-secondary-bg)", border: "var(--bs-border-color)", fg: "var(--bs-secondary-color)", dashed: false },
  out: { bg: "var(--bs-primary-bg-subtle)", border: "var(--bs-primary)", fg: "var(--bs-primary-text-emphasis)", dashed: false },
  out_late: { bg: "var(--bs-primary-bg-subtle)", border: "var(--bs-danger)", fg: "var(--bs-primary-text-emphasis)", dashed: false, thick: true },
  staged: { bg: "var(--bs-secondary-bg)", border: "var(--bs-secondary-color)", fg: "var(--bs-secondary-color)", dashed: true },
};

const ICONS = {
  done: "bi-check-lg",
  active: "bi-play-fill",
  late: "bi-exclamation-circle",
  blocked: "bi-lock-fill",
  cancelled: "bi-x-lg",
  join: "bi-arrow-return-right",
  out: "bi-truck",
  out_late: "bi-truck",
  staged: "bi-box-seam",
};

export default function RoutingStrip({ steps = [], onStepClick, ariaLabel = "Routing", compact = false }) {
  if (!steps || steps.length === 0) return null;
  return (
    <div className="d-flex align-items-center flex-wrap" role="list" aria-label={ariaLabel} style={{ rowGap: 8 }}>
      {steps.map((s, i) => {
        const st = CHIP_STATES[s.state] || CHIP_STATES.pending;
        const clickable = typeof onStepClick === "function" && s.state !== "join";
        const icon = ICONS[s.state];
        const chipStyle = {
          position: "relative",
          flex: "none",
          minWidth: compact ? 64 : 96,
          padding: compact ? "3px 7px" : "4px 9px",
          borderRadius: 6,
          background: st.bg,
          color: st.fg,
          border: `${st.thick ? 2 : 1}px ${st.dashed ? "dashed" : "solid"} ${st.border}`,
          font: "inherit",
          lineHeight: 1.2,
          cursor: clickable ? "pointer" : "default",
          textDecoration: st.strike ? "line-through" : "none",
          textAlign: "left",
        };
        const body = (
          <>
            <span className="d-block fw-semibold text-uppercase" style={{ fontSize: "0.7rem", letterSpacing: ".04em", whiteSpace: "nowrap" }}>
              {icon && <i className={`bi ${icon} me-1`} aria-hidden="true"></i>}
              {s.label}
            </span>
            {(s.qtyLabel != null || s.qty != null) && (
              <span className="d-block fw-bold" style={{ fontSize: "0.9rem", lineHeight: 1.15, whiteSpace: "nowrap" }}>
                {s.qtyLabel != null ? s.qtyLabel : s.qty}
              </span>
            )}
            {s.sub && (
              <span className="d-block" style={{ fontSize: "0.68rem", opacity: 0.85, whiteSpace: "nowrap" }}>
                {s.sub}
              </span>
            )}
            {s.sets && (
              <span
                className="badge rounded-pill bg-primary"
                style={{ position: "absolute", top: -8, right: -6, fontSize: "0.6rem" }}
                title={`${s.sets.done} of ${s.sets.total} sets done`}
              >
                {s.sets.done}/{s.sets.total} sets
              </span>
            )}
            {s.defects > 0 && (
              <span
                className="badge rounded-pill bg-danger"
                style={{ position: "absolute", top: -8, left: -6, fontSize: "0.6rem" }}
                title={`${s.defects} defective`}
              >
                {s.defects} def
              </span>
            )}
          </>
        );
        return (
          <span role="listitem" key={s.key || i} className="d-flex align-items-center">
            {i > 0 && <span aria-hidden="true" style={{ width: compact ? 10 : 16, height: 2, background: "var(--bs-border-color)", flex: "none" }} />}
            {clickable ? (
              <button type="button" onClick={() => onStepClick(s)} title={s.title || s.label} style={chipStyle} data-state={s.state}>
                {body}
              </button>
            ) : (
              <span title={s.title || s.label} style={chipStyle} data-state={s.state}>
                {body}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
